/**
 * InternalNotesPanel — iter-184 P1
 * ────────────────────────────────
 * Lightweight internal operational notes surface, gated by the V1
 * `can_view_internal_notes` capability. Visible to org members of the
 * deal's owning org (always `internal` visibility) and to any
 * participant explicitly granted internal access.
 *
 * For unauthorised participants we render a calm restricted-state
 * placeholder — never a warning banner, never enterprise denial UI.
 *
 * UX:
 *   • single composer row (calm slate input + violet ghost CTA)
 *   • reverse-chronological feed of small note cards
 *   • each card carries author + relative time
 *   • restricted-state placeholder reads
 *     "Internal operational notes are restricted."
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lock, Send, Loader2, ShieldCheck } from "lucide-react";
import { api, apiError } from "../../lib/api";

const fmtRel = (iso) => {
  if (!iso) return "—";
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "—";
  const diff = Math.max(0, Date.now() - ms);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export const InternalNotesPanel = ({ dealId, access }) => {
  const allowed = access?.can_view_internal_notes === true;
  const loading = access?.loading === true;

  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!dealId || !allowed) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/api/deals/${dealId}/internal-notes`);
      setNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      // 403 means access changed mid-session — surface a calm note.
      if (err?.response?.status === 403) {
        setNotes([]);
      } else {
        toast.error("Couldn't load internal notes", { description: apiError(err) });
      }
    } finally {
      setBusy(false);
    }
  }, [dealId, allowed]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const submit = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body) return;
    // iter-201 — optimistic insert. The note appears in the feed
    // the moment the user hits Post, with a calm "sending" state.
    // Server reconciliation runs in parallel; on failure we roll
    // the optimistic entry back and surface a calm inline error.
    const tempId = `optimistic-${Date.now()}`;
    const optimisticNote = {
      id: tempId,
      body,
      author_name: "You",
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setNotes((prev) => [optimisticNote, ...prev]);
    setDraft("");
    setPosting(true);
    try {
      const { data } = await api.post(`/api/deals/${dealId}/internal-notes`, { body });
      // Reconcile — replace the optimistic placeholder with the
      // server-confirmed note (keeps id stability for keys).
      setNotes((prev) => prev.map((n) => (n.id === tempId ? data : n)));
    } catch (err) {
      // Roll back the optimistic entry.
      setNotes((prev) => prev.filter((n) => n.id !== tempId));
      setDraft(body);
      toast.error("Couldn't post note", { description: apiError(err) });
    } finally {
      setPosting(false);
    }
  };

  // Restricted state — calm, no warning copy.
  if (!loading && !allowed) {
    return (
      <section
        className="rounded-2xl bg-slate-50/70 border border-slate-200/80 px-4 py-5"
        data-testid="internal-notes-restricted"
      >
        <div className="flex items-center gap-2.5">
          <Lock size={14} className="text-slate-400" />
          <h3 className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Internal notes
          </h3>
        </div>
        <p className="mt-2 text-[12.5px] text-slate-500 leading-snug">
          Internal operational notes are restricted.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl bg-white border border-slate-200/80 px-4 py-5"
      data-testid="internal-notes-panel"
    >
      <div className="flex items-center gap-2.5">
        <ShieldCheck size={14} className="text-indigo-500" />
        <h3 className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Internal notes
        </h3>
        <span className="ml-auto text-[10.5px] text-slate-400 tracking-tight">
          Visible to your team only
        </span>
      </div>

      {/* Composer */}
      <form onSubmit={submit} className="mt-3 flex items-start gap-2" data-testid="internal-notes-composer">
        <textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Capture an operational note for the team…"
          className="flex-1 resize-none rounded-xl bg-slate-50/70 border border-slate-200 text-[13px] px-3 py-2 placeholder:text-slate-400 focus:bg-white focus:border-indigo-300 outline-none transition-colors"
          data-testid="internal-notes-input"
        />
        <button
          type="submit"
          disabled={posting || !draft.trim()}
          className="h-[60px] px-3.5 rounded-xl text-[12px] font-semibold text-white inline-flex items-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(135deg, #6366F1 0%, #6366F1 50%, #3B82F6 100%)",
            boxShadow: "0 8px 22px -10px rgba(99, 102, 241,0.45)",
          }}
          data-testid="internal-notes-submit"
        >
          {posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          Post
        </button>
      </form>

      {/* Feed */}
      <div className="mt-3.5 space-y-2" data-testid="internal-notes-feed">
        {busy && notes.length === 0 && (
          <p className="text-[12px] text-slate-400">Loading…</p>
        )}
        {!busy && notes.length === 0 && (
          <p className="text-[12px] text-slate-400 italic">
            No internal notes yet — the first one is yours to capture.
          </p>
        )}
        {notes.map((n) => (
          <div
            key={n.id}
            className={`rounded-xl bg-slate-50/60 border border-slate-200/80 px-3 py-2.5 transition-opacity ${
              n._optimistic ? "opacity-80" : ""
            }`}
            data-testid={`internal-note-${n.id}`}
            data-optimistic={n._optimistic ? "1" : "0"}
          >
            <p className="text-[12.5px] text-slate-700 leading-snug whitespace-pre-wrap">{n.body}</p>
            <p className="mt-1.5 text-[10.5px] text-slate-400 tracking-tight inline-flex items-center gap-1.5">
              <span>{n.author_name || "Internal team"}</span>
              <span aria-hidden>·</span>
              {n._optimistic ? (
                <span className="inline-flex items-center gap-1 text-indigo-500" data-testid={`internal-note-sending-${n.id}`}>
                  <Loader2 size={9} className="animate-spin" /> sending…
                </span>
              ) : (
                <span>{fmtRel(n.created_at)}</span>
              )}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default InternalNotesPanel;
